import { endOfTomorrow, subYears } from 'date-fns'
import { useIntl } from 'react-intl'

import { ButtonGroup, FormattedMessage } from '@subaio/components'
import { Form } from '@subaio/components/src/HookForm'
import { getIsoDate } from '@subaio/utils'

import type {
	Handlers,
	OpportunityCarInsuranceStep,
	OpportunityCarInsuranceStepType,
} from 'containers/OpportunityCarInsurance/handlers'
import type { PersonalDetails } from 'containers/OpportunityCarInsurance/types'

import { ButtonSolid, ButtonText } from 'components/Buttons'
import { Card } from 'components/Card'
import { Input } from 'components/FormFields'
import { Header } from 'components/Layout'

import { Title } from '../Title'

type Props = TaggedType<OpportunityCarInsuranceStep, OpportunityCarInsuranceStepType.PersonalForm> & {
	onShowLicensePlate: Handlers['onShowLicensePlate']
	onStepBack?: () => void
}

export const PersonalForm = ({ selectedInsurer, onShowLicensePlate, onStepBack }: Props) => {
	const { formatMessage } = useIntl()

	return (
		<Form<PersonalDetails>
			validationMode='all'
			render={(_, { formState }) => (
				<Card>
					<Header>
						<Title>
							<FormattedMessage id='opportunityCarInsurance.personalForm.title' defaultMessage='Personal data' />
						</Title>
					</Header>
					<Input
						name='address.zipCode'
						type='text'
						label={
							<FormattedMessage id='opportunityCarInsurance.personalForm.labelZipCode' defaultMessage='Zip code' />
						}
						validate={(value) => {
							// We check if the value is a valid zip code
							if (/^[1-9][0-9]{3} ?[a-zA-Z]{2}$/.exec(value) === null) {
								return formatMessage({
									id: 'opportunityCarInsurance.personalForm.validationZipCode',
									defaultMessage: 'Enter a valid zip code',
								})
							}
							return true
						}}
						isRequired
					/>
					<Input
						name='address.houseNumber'
						type='number'
						label={
							<FormattedMessage
								id='opportunityCarInsurance.personalForm.labelHouseNumber'
								defaultMessage='House number'
							/>
						}
					/>
					<Input
						name='address.houseNumberAddition'
						type='text'
						label={
							<FormattedMessage
								id='opportunityCarInsurance.personalForm.labelHouseNumberAddition'
								defaultMessage='Addition'
							/>
						}
					/>
					<Input
						name='birthDate'
						type='date-written'
						placeholder={formatMessage({ id: 'placeholderBirthDate', defaultMessage: 'dd-mm-yyyy' })}
						placeholderAriaLabel={formatMessage({
							id: 'placeholderBirthDateLabel',
							defaultMessage: 'day, month, year.',
						})}
						maxDate={getIsoDate(subYears(endOfTomorrow(), 18))}
						label={
							<FormattedMessage id='opportunityCarInsurance.personalForm.labelBirthDate' defaultMessage='Birth date' />
						}
						isRequired
					/>
					<ButtonGroup center>
						{onStepBack && (
							<ButtonText
								label={<FormattedMessage id='opportunityCarInsurance.buttonBack' defaultMessage='Back' />}
								onClick={() => onStepBack()}
							/>
						)}
						<ButtonSolid
							label={<FormattedMessage id='opportunityCarInsurance.buttonContinue' defaultMessage='Continue' />}
							disabled={!formState.isValid}
							submit
						/>
					</ButtonGroup>
				</Card>
			)}
			onSubmit={(personalDetails) => onShowLicensePlate(selectedInsurer, personalDetails)}
		/>
	)
}
